/**
 * @krisspy-file
 * @type page
 * @name "IssueDetailPage"
 * @title "Issue Detail"
 * @description "Mobile-first issue detail view with status pipeline, collapsible sections, sticky action bar, and bottom-sheet action forms"
 * @routes ["/issues/:id"]
 * @layout "DashboardLayout"
 */

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, AlertCircle, Loader2, Package, MapPin,
  Calendar, Pencil, Check, X as XIcon, Wrench, RotateCcw,
  MessageSquare, Send, Image as ImageIcon, ChevronDown,
  Lock, User
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useDateFormatter } from '../hooks/useDateFormatter';
import {
  getIssueById, confirmIssue, dismissIssue, assignRepair,
  resolveIssue, closeIssue, reopenIssue, addComment,
  getIssuePhotoUrl, updateIssue, uploadIssuePhoto,
  type IssueWithRelations,
} from '../services/issues.service';
import { getUsers, type UserWithRole } from '../services/users.service';
import IssueAffectedBookings from '../components/IssueAffectedBookings';
import StatusPipeline from '../components/StatusPipeline';
import BottomSheet from '../components/BottomSheet';

// ============================================================================
// Config
// ============================================================================

const STATUS_CONFIG: Record<string, { labelKey: string; color: string; bg: string }> = {
  reported: { labelKey: 'issues.statusReported', color: 'text-warning', bg: 'bg-warning/10' },
  confirmed: { labelKey: 'issues.statusConfirmed', color: 'text-blue', bg: 'bg-blue/10' },
  dismissed: { labelKey: 'issues.statusDismissed', color: 'text-purple-600', bg: 'bg-purple-100' },
  repair_assigned: { labelKey: 'issues.statusRepairAssigned', color: 'text-danger', bg: 'bg-danger/10' },
  resolved: { labelKey: 'issues.statusResolved', color: 'text-success', bg: 'bg-success/10' },
  cannot_repair: { labelKey: 'issues.statusResolved', color: 'text-success', bg: 'bg-success/10' },
  closed: { labelKey: 'issues.statusClosed', color: 'text-gray-700', bg: 'bg-gray-200' },
};

const SEVERITY_CONFIG: Record<string, { labelKey: string; color: string; dot: string }> = {
  low: { labelKey: 'issues.severityLow', color: 'text-secondary', dot: 'bg-secondary' },
  medium: { labelKey: 'issues.severityMedium', color: 'text-warning', dot: 'bg-warning' },
  high: { labelKey: 'issues.severityHigh', color: 'text-danger', dot: 'bg-danger' },
  critical: { labelKey: 'issues.severityCritical', color: 'text-danger font-bold', dot: 'bg-danger' },
};

const SOURCE_CONFIG: Record<string, string> = {
  internal: 'issues.internal',
  customer_return: 'issues.customerReturn',
  system: 'issues.system',
};

const CATEGORY_CONFIG: Record<string, string> = {
  damage: 'issueDetail.categoryDamage',
  malfunction: 'issueDetail.categoryMalfunction',
  wear: 'issueDetail.categoryWear',
  missing_part: 'issueDetail.categoryMissingPart',
  cosmetic: 'issueDetail.categoryCosmetic',
  scheduled_check: 'issueDetail.categoryScheduledCheck',
  other: 'issueDetail.categoryOther',
};

// ============================================================================
// Page Component
// ============================================================================

export default function IssueDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { merchantId, accessControl } = useAuth();
  const { showToast } = useToast();
  const { formatDate, formatDateTimeCompact } = useDateFormatter();
  const { t } = useTranslation();

  const [issue, setIssue] = useState<IssueWithRelations | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Collapsible sections
  const [showBookings, setShowBookings] = useState(false);

  // Comments
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  const commentInputRef = useRef<HTMLInputElement>(null);

  // Photo upload in comment area
  const photoInputRef = useRef<HTMLInputElement>(null);
  const photoInputMobileRef = useRef<HTMLInputElement>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Due date editing
  const [editingDueDate, setEditingDueDate] = useState(false);
  const [dueDateValue, setDueDateValue] = useState('');

  // Bottom sheet action forms
  const [activeSheet, setActiveSheet] = useState<'confirm' | 'dismiss' | 'assign' | 'resolve' | null>(null);

  // Form states
  const [confirmNotes, setConfirmNotes] = useState('');
  const [dismissNotes, setDismissNotes] = useState('');
  const [assigneeName, setAssigneeName] = useState('');
  const [assigneeType, setAssigneeType] = useState<'internal' | 'external'>('internal');
  const [assignedToUserId, setAssignedToUserId] = useState<string>('');
  const [repairNotes, setRepairNotes] = useState('');
  const [assignDueDate, setAssignDueDate] = useState('');
  const [teamMembers, setTeamMembers] = useState<UserWithRole[]>([]);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [resolutionType, setResolutionType] = useState<'repaired' | 'replaced_part' | 'retired_item' | 'no_action_needed' | 'cannot_repair'>('repaired');
  const [resetCheckCounter, setResetCheckCounter] = useState(false);

  // Inline editing
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionValue, setDescriptionValue] = useState('');
  const [editingSeverity, setEditingSeverity] = useState(false);
  const [severityValue, setSeverityValue] = useState('');
  const [editingRepairNotes, setEditingRepairNotes] = useState(false);
  const [repairNotesValue, setRepairNotesValue] = useState('');

  // ============================================================================
  // Data loading
  // ============================================================================

  useEffect(() => {
    if (id) loadIssue();
    loadUserId();
    loadTeamMembers();
  }, [id]);

  async function loadUserId() {
    const { data } = await supabase.auth.getUser();
    setUserId(data?.user?.id || null);
  }

  async function loadTeamMembers() {
    const { data } = await getUsers(accessControl);
    if (data) setTeamMembers(data);
  }

  async function loadIssue() {
    if (!id) return;
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await getIssueById(id);
    if (fetchError) {
      setError(fetchError);
      setLoading(false);
      return;
    }
    if (data) {
      setIssue(data);
      setResetCheckCounter(data.category === 'scheduled_check');
    }
    setLoading(false);
  }

  // ============================================================================
  // Actions
  // ============================================================================

  async function handleConfirm() {
    if (!userId) return;
    setActionLoading(true);
    const { error } = await confirmIssue(id!, userId, confirmNotes || undefined);
    if (error) showToast('Failed: ' + error, 'error');
    else { showToast('Issue confirmed', 'success'); setActiveSheet(null); setConfirmNotes(''); }
    await loadIssue();
    setActionLoading(false);
  }

  async function handleDismiss() {
    if (!userId) return;
    setActionLoading(true);
    const { error } = await dismissIssue(id!, userId, dismissNotes || undefined);
    if (error) showToast('Failed: ' + error, 'error');
    else { showToast('Issue dismissed', 'success'); setActiveSheet(null); setDismissNotes(''); }
    await loadIssue();
    setActionLoading(false);
  }

  async function handleAssign() {
    if (!userId || !assigneeName.trim()) return;
    setActionLoading(true);
    const { error } = await assignRepair(id!, userId, {
      assignee_type: assigneeType,
      assignee_name: assigneeName.trim(),
      assigned_to_user_id: assigneeType === 'internal' && assignedToUserId ? assignedToUserId : null,
      repair_notes: repairNotes || undefined,
      repair_due_date: assignDueDate || undefined,
    });
    if (error) showToast('Failed: ' + error, 'error');
    else {
      showToast('Repair assigned', 'success');
      setActiveSheet(null);
      setAssigneeName('');
      setAssignedToUserId('');
      setRepairNotes('');
      setAssignDueDate('');
    }
    await loadIssue();
    setActionLoading(false);
  }

  async function handleResolve() {
    if (!userId) return;
    setActionLoading(true);
    const { error } = await resolveIssue(id!, userId, {
      resolution_notes: resolutionNotes || undefined,
      resolution_type: resolutionType,
      reset_check_counter: resetCheckCounter,
    });
    if (error) showToast('Failed: ' + error, 'error');
    else { showToast('Issue resolved', 'success'); setActiveSheet(null); setResolutionNotes(''); }
    await loadIssue();
    setActionLoading(false);
  }

  async function handleClose() {
    setActionLoading(true);
    const { error } = await closeIssue(id!, userId || undefined);
    if (error) showToast('Failed: ' + error, 'error');
    else showToast('Issue closed', 'success');
    await loadIssue();
    setActionLoading(false);
  }

  async function handleReopen() {
    if (!userId) return;
    setActionLoading(true);
    const { error } = await reopenIssue(id!, userId);
    if (error) showToast('Failed: ' + error, 'error');
    else showToast('Issue reopened', 'success');
    await loadIssue();
    setActionLoading(false);
  }

  function openAssignSheetPrefilled() {
    if (!issue) return;
    setAssigneeName(issue.assignee_name || '');
    setAssigneeType((issue.assignee_type as 'internal' | 'external') || 'internal');
    setAssignedToUserId(issue.assigned_to_user_id || '');
    setRepairNotes(issue.repair_notes || '');
    setAssignDueDate(issue.repair_due_date || '');
    setActiveSheet('assign');
  }

  async function handleSaveField(field: string, value: string | null) {
    setActionLoading(true);
    const { error } = await updateIssue(id!, { [field]: value });
    if (error) showToast('Failed to update', 'error');
    else showToast('Updated', 'success');
    await loadIssue();
    setActionLoading(false);
  }

  async function handleAddComment() {
    if (!userId || !newComment.trim()) return;
    setAddingComment(true);
    const { error } = await addComment(id!, userId, newComment.trim());
    if (error) showToast('Failed to add comment', 'error');
    else setNewComment('');
    await loadIssue();
    setAddingComment(false);
  }

  async function handlePhotoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !userId || !merchantId) return;
    e.target.value = ''; // reset so same file can be re-selected
    setUploadingPhoto(true);
    const { error } = await uploadIssuePhoto(id!, merchantId, userId, file);
    if (error) showToast('Failed to upload photo: ' + error, 'error');
    else showToast('Photo added', 'success');
    await loadIssue();
    setUploadingPhoto(false);
  }

  async function handleSaveDueDate() {
    setActionLoading(true);
    const { error } = await updateIssue(id!, { repair_due_date: dueDateValue || null });
    if (error) showToast('Failed to update due date', 'error');
    else showToast(dueDateValue ? 'Due date updated' : 'Due date removed', 'success');
    setEditingDueDate(false);
    await loadIssue();
    setActionLoading(false);
  }

  function handleBlockingChanged(newBlockingTimeId: string | null) {
    if (issue) {
      setIssue({ ...issue, blocking_time_id: newBlockingTimeId });
    }
  }

  const timeAgo = (d: string) => formatDateTimeCompact(d);

  const isTerminal = issue ? ['closed', 'dismissed', 'resolved', 'cannot_repair'].includes(issue.status) : false;
  const canReopen = issue ? ['closed', 'dismissed', 'resolved', 'cannot_repair'].includes(issue.status) : false;

  // ============================================================================
  // Loading & Error States
  // ============================================================================

  if (loading) {
    return (
      <div className="space-y-2xl animate-fade-in">
        <div className="card">
          <div className="flex items-center justify-center py-2xl">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
            <span className="ml-md text-secondary">{t('issueDetail.loadingIssue')}</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !issue) {
    return (
      <div className="space-y-2xl animate-fade-in">
        <div className="card bg-danger/10 border border-danger/20">
          <div className="flex items-center gap-md">
            <AlertCircle className="w-5 h-5 text-danger flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-danger mb-xs">{t('common.error')}</h4>
              <p className="text-sm text-danger/80">{error || t('issueDetail.notFound')}</p>
            </div>
            <button onClick={() => navigate('/issues')} className="btn btn-ghost text-danger hover:bg-danger/10">
              {t('issueDetail.backToIssues')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const sc = STATUS_CONFIG[issue.status] || STATUS_CONFIG.reported;
  const sev = SEVERITY_CONFIG[issue.severity] || SEVERITY_CONFIG.medium;

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="space-y-lg animate-fade-in pb-40 lg:pb-lg">
      {/* ── Page Header ── */}
      <div className="page-header">
        <div className="page-header-title flex items-start gap-lg">
          <button onClick={() => navigate('/issues')} className="btn-icon mt-1 flex-shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-sm mb-xs flex-wrap">
              <h1 className="text-xl sm:text-2xl font-bold text-primary break-words">{issue.title}</h1>
              <span className={`inline-flex items-center px-sm py-xs rounded-full text-xs font-medium ${sc.bg} ${sc.color}`}>
                {t(sc.labelKey)}
              </span>
            </div>
            <div className="flex items-center gap-sm flex-wrap text-sm text-secondary">
              {editingSeverity ? (
                <span className="flex items-center gap-xs">
                  <select
                    value={severityValue}
                    onChange={(e) => setSeverityValue(e.target.value)}
                    className="input text-sm py-0 px-sm"
                  >
                    <option value="low">{t('issues.severityLow')}</option>
                    <option value="medium">{t('issues.severityMedium')}</option>
                    <option value="high">{t('issues.severityHigh')}</option>
                    <option value="critical">{t('issues.severityCritical')}</option>
                  </select>
                  <button onClick={() => { handleSaveField('severity', severityValue); setEditingSeverity(false); }} className="btn-icon text-success" disabled={actionLoading}>
                    <Check className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => setEditingSeverity(false)} className="btn-icon text-secondary">
                    <XIcon className="w-3.5 h-3.5" />
                  </button>
                </span>
              ) : (
                <button
                  onClick={() => { setSeverityValue(issue.severity); setEditingSeverity(true); }}
                  className="flex items-center gap-xs hover:bg-secondary/10 rounded px-xs -mx-xs transition-colors"
                >
                  <div className={`w-2 h-2 rounded-full ${sev.dot}`} />
                  {t(sev.labelKey)}
                  <Pencil className="w-2.5 h-2.5 text-secondary opacity-0 group-hover:opacity-100" />
                </button>
              )}
              {issue.category && (
                <>
                  <span>·</span>
                  <span>{t(CATEGORY_CONFIG[issue.category] || issue.category)}</span>
                </>
              )}
              <span>·</span>
              <span>{t(SOURCE_CONFIG[issue.source] || issue.source)}</span>
              <span>·</span>
              <span>{timeAgo(issue.created_at)}</span>
              {issue.reported_by_profile && (
                <>
                  <span>{t('issueDetail.by')}</span>
                  <span>{issue.reported_by_profile.full_name}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Status Pipeline ── */}
      <div className="card p-md">
        <StatusPipeline status={issue.status} />
      </div>

      {/* ── Main Content Grid ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-lg">
        {/* Left Column (2/3 on desktop) */}
        <div className="lg:col-span-2 space-y-lg">

          {/* Description */}
          {(issue.description || !isTerminal) && (
            <div className="card">
              <div className="flex items-center justify-between mb-sm">
                <h2 className="text-sm font-semibold text-secondary">{t('issueDetail.description')}</h2>
                {!editingDescription && (
                  <button
                    onClick={() => { setDescriptionValue(issue.description || ''); setEditingDescription(true); }}
                    className="btn-icon text-secondary"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              {editingDescription ? (
                <div className="space-y-sm">
                  <textarea
                    value={descriptionValue}
                    onChange={(e) => setDescriptionValue(e.target.value)}
                    className="input w-full text-sm"
                    rows={4}
                    placeholder={t('issueDetail.descriptionPlaceholder')}
                  />
                  <div className="flex gap-sm">
                    <button onClick={() => { handleSaveField('description', descriptionValue || null); setEditingDescription(false); }} className="btn btn-sm btn-primary" disabled={actionLoading}>
                      {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.save')}
                    </button>
                    <button onClick={() => setEditingDescription(false)} className="btn btn-sm btn-ghost">{t('common.cancel')}</button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-primary whitespace-pre-wrap">{issue.description || <span className="text-secondary italic">{t('issueDetail.noDescription')}</span>}</p>
              )}
            </div>
          )}

          {/* Item & Location Card */}
          {(issue.inventory_items || issue.product_types || issue.locations) && (
            <div className="card space-y-md">
              <h2 className="text-sm font-semibold text-secondary">{t('issueDetail.itemDetails')}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-md">
                {issue.inventory_items && (
                  <div className="flex items-center gap-sm">
                    <Package className="w-4 h-4 text-secondary flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-primary">
                        {issue.inventory_items.item_number}
                        {issue.inventory_items.size && (
                          <span className="text-secondary font-normal ml-md">({issue.inventory_items.size})</span>
                        )}
                      </p>
                      {issue.product_types && (
                        <p className="text-xs text-secondary">{issue.product_types.product_name}</p>
                      )}
                    </div>
                  </div>
                )}
                {issue.locations && (
                  <div className="flex items-center gap-sm">
                    <MapPin className="w-4 h-4 text-secondary flex-shrink-0" />
                    <p className="text-sm text-primary">{issue.locations.location_name}</p>
                  </div>
                )}
                {issue.bookings && (
                  <div className="flex items-center gap-sm">
                    <Calendar className="w-4 h-4 text-secondary flex-shrink-0" />
                    <div>
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/bookings/${issue.booking_id}/edit`); }}
                        className="text-sm font-medium text-blue hover:underline"
                      >
                        {t('issueDetail.bookingNumber')}{issue.bookings.order_id}
                      </button>
                      <p className="text-xs text-secondary">
                        {issue.bookings.customer_firstname} {issue.bookings.customer_lastname}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Assignment Card */}
          {issue.assignee_name && (
            <div className="card space-y-md">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-secondary">{t('issueDetail.assignment')}</h2>
                {issue.status === 'repair_assigned' && (
                  <button onClick={openAssignSheetPrefilled} className="btn btn-sm btn-ghost text-warning hover:bg-warning/10">
                    <Wrench className="w-3.5 h-3.5" /> {t('issueDetail.reassign')}
                  </button>
                )}
              </div>
              <div className="space-y-sm">
                <div className="flex items-center gap-sm">
                  <User className="w-4 h-4 text-secondary flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-primary">{issue.assignee_name}</p>
                    <p className="text-xs text-secondary">{issue.assignee_type === 'internal' ? t('issueDetail.internal') : t('issueDetail.external')}</p>
                  </div>
                </div>
                {editingRepairNotes ? (
                  <div className="space-y-sm">
                    <div>
                      <p className="text-xs text-secondary mb-xs">{t('issueDetail.repairInstructions')}</p>
                      <textarea
                        value={repairNotesValue}
                        onChange={(e) => setRepairNotesValue(e.target.value)}
                        className="input w-full text-sm"
                        rows={3}
                        placeholder={t('issueDetail.repairInstructions')}
                      />
                    </div>
                    <div className="flex gap-sm">
                      <button onClick={() => { handleSaveField('repair_notes', repairNotesValue || null); setEditingRepairNotes(false); }} className="btn btn-sm btn-primary" disabled={actionLoading}>
                        {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.save')}
                      </button>
                      <button onClick={() => setEditingRepairNotes(false)} className="btn btn-sm btn-ghost">{t('common.cancel')}</button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-secondary mb-xs">{t('issueDetail.repairInstructions')}</p>
                      <button
                        onClick={() => { setRepairNotesValue(issue.repair_notes || ''); setEditingRepairNotes(true); }}
                        className="btn-icon text-secondary"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                    </div>
                    <p className="text-sm text-primary whitespace-pre-wrap">{issue.repair_notes || <span className="text-secondary italic">{t('common.none')}</span>}</p>
                  </div>
                )}
                {issue.assigned_by_profile && (
                  <p className="text-xs text-secondary">
                    {t('issueDetail.assignedBy')} {issue.assigned_by_profile.full_name}
                    {issue.assigned_at && <> · {timeAgo(issue.assigned_at)}</>}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Due Date Card (tappable, not hover-dependent) */}
          {issue.status !== 'closed' && (
            <div className="card">
              <div className="flex items-center justify-between flex-wrap gap-sm">
                <div className="flex items-center gap-sm">
                  <Calendar className="w-4 h-4 text-secondary" />
                  <span className="text-sm font-semibold text-secondary">{t('issueDetail.dueDate')}</span>
                  {/* Overdue badge — shown outside the edit button for clarity */}
                  {!editingDueDate && issue.repair_due_date && new Date(issue.repair_due_date) < new Date() && (
                    <span className="inline-flex items-center px-sm py-0.5 rounded-full text-xs font-semibold bg-danger/10 text-danger">
                      {t('issueDetail.overdue')}
                    </span>
                  )}
                </div>
                {editingDueDate ? (
                  <div className="flex items-center gap-xs">
                    <input
                      type="date"
                      value={dueDateValue}
                      onChange={(e) => setDueDateValue(e.target.value)}
                      className="input text-sm py-xs px-sm"
                    />
                    <button onClick={handleSaveDueDate} className="btn-icon text-success" disabled={actionLoading}>
                      <Check className="w-4 h-4" />
                    </button>
                    <button onClick={() => setEditingDueDate(false)} className="btn-icon text-secondary">
                      <XIcon className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => { setDueDateValue(issue.repair_due_date || ''); setEditingDueDate(true); }}
                    className="flex items-center gap-sm px-sm py-xs rounded-lg border border-border hover:border-blue transition-colors"
                  >
                    <span className={`text-sm font-medium ${issue.repair_due_date && new Date(issue.repair_due_date) < new Date() ? 'text-danger' : 'text-primary'}`}>
                      {issue.repair_due_date ? formatDate(issue.repair_due_date) : t('issueDetail.notSet')}
                    </span>
                    <Pencil className="w-3 h-3 text-secondary" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Closed: show due date read-only if present */}
          {issue.status === 'closed' && issue.repair_due_date && (
            <div className="card">
              <div className="flex items-center gap-sm">
                <Calendar className="w-4 h-4 text-secondary" />
                <span className="text-sm text-secondary">{t('issueDetail.dueDateLabel')}</span>
                <span className="text-sm font-medium text-primary">{formatDate(issue.repair_due_date)}</span>
              </div>
            </div>
          )}

          {/* Confirmation Notes */}
          {issue.confirmation_notes && (
            <div className="card">
              <h2 className="text-sm font-semibold text-secondary mb-sm">{t('issueDetail.confirmationNotes')}</h2>
              <p className="text-sm text-primary whitespace-pre-wrap">{issue.confirmation_notes}</p>
              {issue.confirmed_by_profile && (
                <p className="text-xs text-secondary mt-sm">
                  {t('issueDetail.confirmedBy')} {issue.confirmed_by_profile.full_name}
                  {issue.confirmed_at && <> · {timeAgo(issue.confirmed_at)}</>}
                </p>
              )}
            </div>
          )}

          {/* Resolution */}
          {issue.resolution_notes && (
            <div className="card">
              <h2 className="text-sm font-semibold text-secondary mb-sm">{t('issueDetail.resolution')}</h2>
              <p className="text-sm text-primary whitespace-pre-wrap">{issue.resolution_notes}</p>
              {issue.resolution_type && (
                <span className="inline-block mt-sm px-sm py-xs rounded-full text-xs font-medium bg-success/10 text-success">
                  {issue.resolution_type.replace(/_/g, ' ')}
                </span>
              )}
              {issue.resolved_by_profile && (
                <p className="text-xs text-secondary mt-sm">
                  {t('issueDetail.resolvedBy')} {issue.resolved_by_profile.full_name}
                  {issue.resolved_at && <> · {timeAgo(issue.resolved_at)}</>}
                </p>
              )}
            </div>
          )}

          {/* Photos */}
          {issue.issue_photos && issue.issue_photos.length > 0 && (
            <div className="card">
              <h2 className="text-sm font-semibold text-secondary mb-sm flex items-center gap-xs">
                <ImageIcon className="w-4 h-4" /> {t('issueDetail.photos')} ({issue.issue_photos.length})
              </h2>
              <div className="flex gap-sm overflow-x-auto pb-sm -mx-sm px-sm">
                {issue.issue_photos.map((photo) => (
                  <a
                    key={photo.id}
                    href={getIssuePhotoUrl(photo.file_path)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-24 h-24 rounded-lg overflow-hidden border border-border hover:border-blue transition-colors flex-shrink-0"
                  >
                    <img
                      src={getIssuePhotoUrl(photo.file_path)}
                      alt={photo.file_name}
                      className="w-full h-full object-cover"
                    />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Affected Bookings (collapsible) */}
          {issue.inventory_item_id && userId && issue.status !== 'closed' && (
            <div className="card p-0 overflow-hidden">
              <button
                onClick={() => setShowBookings(!showBookings)}
                className="w-full flex items-center justify-between p-md hover:bg-secondary/5 transition-colors"
              >
                <div className="flex items-center gap-sm">
                  <Lock className="w-4 h-4 text-secondary" />
                  <span className="text-sm font-semibold text-primary">{t('issueDetail.affectedBookings')}</span>
                  {issue.blocking_time_id && (
                    <span className="px-sm py-0.5 rounded-full text-xs font-medium bg-danger/10 text-danger">{t('issueDetail.blocked')}</span>
                  )}
                </div>
                <ChevronDown className={`w-4 h-4 text-secondary transition-transform ${showBookings ? 'rotate-180' : ''}`} />
              </button>
              {showBookings && (
                <div className="border-t border-border p-md">
                  <IssueAffectedBookings
                    issueId={issue.id}
                    inventoryItemId={issue.inventory_item_id}
                    productTypeId={issue.product_type_id || issue.inventory_items?.product_type_id || null}
                    productName={issue.product_types?.product_name}
                    itemNumber={issue.inventory_items?.item_number || null}
                    itemSize={issue.inventory_items?.size || null}
                    merchantId={merchantId || ''}
                    blockingTimeId={issue.blocking_time_id}
                    repairDueDate={issue.repair_due_date}
                    userId={userId}
                    onBlockingChanged={handleBlockingChanged}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Column (1/3 on desktop) — Activity + Desktop Actions */}
        <div className="space-y-lg">
          {/* Desktop action buttons (hidden on mobile, shown in sticky bar instead) */}
          <div className="hidden lg:block card">
            <h2 className="text-sm font-semibold text-secondary mb-md">{t('issueDetail.actions')}</h2>
            <div className="space-y-sm">
              {issue.status === 'reported' && (
                <>
                  <button onClick={() => setActiveSheet('confirm')} className="btn btn-primary w-full" disabled={actionLoading}>
                    <Check className="w-4 h-4" /> {t('issueDetail.confirmIssue')}
                  </button>
                  <button onClick={() => setActiveSheet('dismiss')} className="btn btn-ghost w-full" disabled={actionLoading}>
                    <XIcon className="w-4 h-4" /> {t('issueDetail.dismiss')}
                  </button>
                </>
              )}
              {['confirmed', 'reported'].includes(issue.status) && (
                <button onClick={() => { setAssignDueDate(issue.repair_due_date || ''); setActiveSheet('assign'); }} className="btn btn-ghost text-warning hover:bg-warning/10 w-full" disabled={actionLoading}>
                  <Wrench className="w-4 h-4" /> {t('issueDetail.assignRepair')}
                </button>
              )}
              {issue.status === 'repair_assigned' && (
                <button onClick={openAssignSheetPrefilled} className="btn btn-ghost text-warning hover:bg-warning/10 w-full" disabled={actionLoading}>
                  <Wrench className="w-4 h-4" /> {t('issueDetail.reassign')}
                </button>
              )}
              {['repair_assigned', 'confirmed'].includes(issue.status) && (
                <button onClick={() => setActiveSheet('resolve')} className="btn btn-primary w-full" disabled={actionLoading}>
                  <Check className="w-4 h-4" /> {t('issueDetail.resolve')}
                </button>
              )}
              {issue.status === 'resolved' && (
                <button onClick={handleClose} className="btn btn-ghost w-full" disabled={actionLoading}>
                  <Check className="w-4 h-4" /> {t('issueDetail.closeIssue')}
                </button>
              )}
              {canReopen && (
                <button onClick={handleReopen} className="btn btn-ghost w-full" disabled={actionLoading}>
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RotateCcw className="w-4 h-4" /> {t('issueDetail.reopen')}</>}
                </button>
              )}
            </div>
          </div>

          {/* Activity Timeline */}
          <div className="card">
            <h2 className="text-sm font-semibold text-secondary mb-md flex items-center gap-xs">
              <MessageSquare className="w-4 h-4" />
              {t('issueDetail.activity')} ({issue.issue_comments?.length || 0})
            </h2>
            <div className="space-y-sm max-h-96 overflow-y-auto">
              {(issue.issue_comments || []).length === 0 && (
                <p className="text-sm text-secondary py-sm">{t('issueDetail.noActivity')}</p>
              )}
              {(issue.issue_comments || []).map((comment) => (
                <div key={comment.id} className={`text-sm p-sm rounded-lg ${comment.is_system ? 'bg-secondary/5 text-secondary italic' : 'bg-secondary/10'}`}>
                  {comment.profiles && !comment.is_system && (
                    <p className="text-xs font-medium text-primary mb-0.5">{comment.profiles.full_name}</p>
                  )}
                  <p className={comment.is_system ? '' : 'text-primary'}>{comment.comment}</p>
                  <p className="text-xs text-secondary mt-0.5">
                    {comment.is_system && comment.profiles && (
                      <span>{t('issueDetail.by')} {comment.profiles.full_name} · </span>
                    )}
                    {timeAgo(comment.created_at)}
                  </p>
                </div>
              ))}
            </div>

            {/* Desktop comment input */}
            <div className="hidden lg:flex gap-sm mt-md pt-md border-t border-border">
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                className="input flex-1 text-sm"
                placeholder={t('issueDetail.addComment')}
              />
              <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
              <button onClick={() => photoInputRef.current?.click()} disabled={uploadingPhoto} className="btn btn-sm btn-ghost" title="Add photo">
                {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
              </button>
              <button onClick={handleAddComment} disabled={addingComment || !newComment.trim()} className="btn btn-sm btn-primary">
                {addingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Sticky Bottom Bar (mobile + tablet, hidden at lg via CSS) ── */}
      {/* Rendered via portal to document.body so it escapes the dashboard layout's
          overflow-y:auto scroll container, which clips fixed elements on iOS Safari.
          Critical positioning uses inline styles to avoid CSS utility issues on iOS. */}
      {createPortal(
        <div
          className="bg-primary border-t border-border p-md space-y-sm lg:hidden"
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 40,
            paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
          }}
        >
          {/* Comment input */}
          <div className="flex gap-sm">
            <input
              ref={commentInputRef}
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
              className="input flex-1 text-sm"
              placeholder={t('issueDetail.addComment')}
            />
            <input ref={photoInputMobileRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
            <button onClick={() => photoInputMobileRef.current?.click()} disabled={uploadingPhoto} className="btn btn-sm btn-ghost" title="Add photo">
              {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
            </button>
            <button onClick={handleAddComment} disabled={addingComment || !newComment.trim()} className="btn btn-sm btn-primary">
              {addingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>

          {/* Action buttons — flex-wrap prevents overflow on small screens */}
          <div className="flex flex-wrap gap-sm">
            {issue.status === 'reported' && (
              <>
                <button onClick={() => setActiveSheet('confirm')} className="btn btn-sm btn-primary flex-1" disabled={actionLoading}>
                  <Check className="w-4 h-4" /> {t('issueDetail.confirmIssue')}
                </button>
                <button onClick={() => setActiveSheet('dismiss')} className="btn btn-sm btn-ghost" disabled={actionLoading}>
                  <XIcon className="w-4 h-4" /> {t('issueDetail.dismiss')}
                </button>
              </>
            )}
            {['confirmed', 'reported'].includes(issue.status) && (
              <button onClick={() => { setAssignDueDate(issue.repair_due_date || ''); setActiveSheet('assign'); }} className="btn btn-sm btn-ghost text-warning hover:bg-warning/10 flex-1" disabled={actionLoading}>
                <Wrench className="w-4 h-4" /> {t('issueDetail.assignRepair')}
              </button>
            )}
            {issue.status === 'repair_assigned' && (
              <button onClick={openAssignSheetPrefilled} className="btn btn-sm btn-ghost text-warning hover:bg-warning/10" disabled={actionLoading}>
                <Wrench className="w-3.5 h-3.5" /> {t('issueDetail.reassign')}
              </button>
            )}
            {['repair_assigned', 'confirmed'].includes(issue.status) && (
              <button onClick={() => setActiveSheet('resolve')} className="btn btn-sm btn-primary flex-1" disabled={actionLoading}>
                <Check className="w-4 h-4" /> {t('issueDetail.resolve')}
              </button>
            )}
            {issue.status === 'resolved' && (
              <button onClick={handleClose} className="btn btn-sm btn-ghost flex-1" disabled={actionLoading}>
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" /> {t('issueDetail.closeIssue')}</>}
              </button>
            )}
            {canReopen && (
              <button onClick={handleReopen} className="btn btn-sm btn-ghost flex-1" disabled={actionLoading}>
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RotateCcw className="w-4 h-4" /> {t('issueDetail.reopen')}</>}
              </button>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* ── Bottom Sheet: Confirm ── */}
      <BottomSheet isOpen={activeSheet === 'confirm'} onClose={() => setActiveSheet(null)} title={t('issueDetail.confirmIssue')}>
        <div className="space-y-lg">
          <p className="text-sm text-secondary">{t('issueDetail.confirmDesc')}</p>
          <div>
            <label className="block text-sm font-medium text-primary mb-xs">{t('issueDetail.notesOptional')}</label>
            <textarea
              value={confirmNotes}
              onChange={(e) => setConfirmNotes(e.target.value)}
              className="input w-full"
              rows={3}
              placeholder={t('issueDetail.confirmNotesPlaceholder')}
            />
          </div>
          <button onClick={handleConfirm} className="btn btn-primary w-full" disabled={actionLoading}>
            {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" /> {t('issueDetail.confirmIssue')}</>}
          </button>
        </div>
      </BottomSheet>

      {/* ── Bottom Sheet: Dismiss ── */}
      <BottomSheet isOpen={activeSheet === 'dismiss'} onClose={() => setActiveSheet(null)} title={t('issueDetail.dismissIssue')}>
        <div className="space-y-lg">
          <p className="text-sm text-secondary">{t('issueDetail.dismissDesc')}</p>
          <div>
            <label className="block text-sm font-medium text-primary mb-xs">{t('issueDetail.reasonOptional')}</label>
            <textarea
              value={dismissNotes}
              onChange={(e) => setDismissNotes(e.target.value)}
              className="input w-full"
              rows={3}
              placeholder={t('issueDetail.dismissPlaceholder')}
            />
          </div>
          <button onClick={handleDismiss} className="btn btn-ghost text-danger hover:bg-danger/10 w-full" disabled={actionLoading}>
            {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><XIcon className="w-4 h-4" /> {t('issueDetail.dismissIssue')}</>}
          </button>
        </div>
      </BottomSheet>

      {/* ── Bottom Sheet: Assign Repair ── */}
      <BottomSheet isOpen={activeSheet === 'assign'} onClose={() => setActiveSheet(null)} title={t('issueDetail.assignRepair')}>
        <div className="space-y-lg">
          <div>
            <label className="block text-sm font-medium text-primary mb-xs">{t('issueDetail.assigneeType')}</label>
            <select
              value={assigneeType}
              onChange={(e) => {
                const type = e.target.value as 'internal' | 'external';
                setAssigneeType(type);
                if (type === 'external') { setAssignedToUserId(''); }
                else { setAssigneeName(''); }
              }}
              className="input w-full"
            >
              <option value="internal">{t('issueDetail.internalTeamMember')}</option>
              <option value="external">{t('issueDetail.external')}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-primary mb-xs">
              {assigneeType === 'internal' ? t('issueDetail.teamMember') : t('issueDetail.name')} <span className="text-danger">*</span>
            </label>
            {assigneeType === 'internal' ? (
              <select
                value={assignedToUserId}
                onChange={(e) => {
                  const uid = e.target.value;
                  setAssignedToUserId(uid);
                  const member = teamMembers.find(m => m.id === uid);
                  setAssigneeName(member?.full_name || '');
                }}
                className="input w-full"
              >
                <option value="">{t('issueDetail.selectTeamMember')}</option>
                {teamMembers.map(m => (
                  <option key={m.id} value={m.id}>
                    {m.full_name || '(no name)'} — {m.role}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={assigneeName}
                onChange={(e) => setAssigneeName(e.target.value)}
                className="input w-full"
                placeholder={t('issueDetail.personOrCompany')}
              />
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-primary mb-xs">{t('issueDetail.repairInstructions')}</label>
            <textarea
              value={repairNotes}
              onChange={(e) => setRepairNotes(e.target.value)}
              className="input w-full"
              rows={3}
              placeholder={t('issueDetail.whatNeedsDone')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-primary mb-xs">
              <span className="flex items-center gap-xs">
                <Calendar className="w-4 h-4" />
                {t('issueDetail.dueDate')}
              </span>
            </label>
            <input
              type="date"
              value={assignDueDate}
              onChange={(e) => setAssignDueDate(e.target.value)}
              className="input w-full"
              min={new Date().toISOString().split('T')[0]}
            />
          </div>
          <button onClick={handleAssign} className="btn btn-primary w-full" disabled={actionLoading || !assigneeName.trim()}>
            {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wrench className="w-4 h-4" /> {t('issueDetail.assignRepair')}</>}
          </button>
        </div>
      </BottomSheet>

      {/* ── Bottom Sheet: Resolve ── */}
      <BottomSheet isOpen={activeSheet === 'resolve'} onClose={() => setActiveSheet(null)} title={t('issueDetail.resolve')}>
        <div className="space-y-lg">
          <div>
            <label className="block text-sm font-medium text-primary mb-xs">{t('issueDetail.resolutionType')}</label>
            <select value={resolutionType} onChange={(e) => setResolutionType(e.target.value as any)} className="input w-full">
              <option value="repaired">{t('issueDetail.repaired')}</option>
              <option value="replaced_part">{t('issueDetail.replacedPart')}</option>
              <option value="retired_item">{t('issueDetail.retiredItem')}</option>
              <option value="no_action_needed">{t('issueDetail.noActionNeeded')}</option>
              <option value="cannot_repair">{t('issueDetail.cannotRepair')}</option>
            </select>
            {resolutionType === 'cannot_repair' && (
              <p className="text-xs text-danger mt-xs">{t('issueDetail.cannotRepairWarning')}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-primary mb-xs">{t('issueDetail.notes')}</label>
            <textarea
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
              className="input w-full"
              rows={3}
              placeholder={resolutionType === 'cannot_repair' ? t('issueDetail.whyCantRepair') : t('issueDetail.whatWasDone')}
            />
          </div>
          {issue.inventory_item_id && (
            <label className="flex items-start gap-sm cursor-pointer p-md border border-border rounded-lg">
              <input
                type="checkbox"
                checked={resetCheckCounter}
                onChange={(e) => setResetCheckCounter(e.target.checked)}
                className="w-4 h-4 mt-0.5 rounded border-primary text-blue focus:ring-blue"
              />
              <div>
                <span className="text-sm font-medium text-primary">{t('issueDetail.resetCheckCounter')}</span>
                <p className="text-xs text-secondary mt-xs">{t('issueDetail.resetCheckCounterDesc')}</p>
              </div>
            </label>
          )}
          <button onClick={handleResolve} className="btn btn-primary w-full" disabled={actionLoading}>
            {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" /> {t('issueDetail.markResolved')}</>}
          </button>
        </div>
      </BottomSheet>

    </div>
  );
}
